import React from "react";
import { Link } from "react-router-dom";

import { Calculator, ArrowUpRight } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import BannerCTA from "../CTA/CTABanner.jsx";
import { ImageInline } from "../GetStarted/GetStartedHelpers.jsx";
import Seo from "../Seo.jsx";
import guidesConfig from "../../config/guidesConfig";
import Tooltip from "./Tooltip";
import "./guides.css";
import crybri from './images/smarkets-cry-bri.png'
import bux from './images/smarkets-bucks.png'
import sm from './images/smarkets.png'
import bf from './images/betfair.png'
import bd from './images/betdaq.png'
import mb from './images/matchbook.png'
import cheTotBack from "./images/che-tot-back.png";
import cheTotLay from "./images/che-tot-lay.png";
import om from "./images/om.png";


const BettingExchangeGuide = () => {
  const meta = guidesConfig.bettingExchangeGuide?.seo || {};

  return (
    <div className="guides-content">
      <Seo title={meta.title} description={meta.description} url={guidesConfig.bettingExchangeGuide?.route || "/guides"}/>
      <p>
        A <strong>betting exchange</strong> is a platform that lets people bet <strong>against each other</strong> instead of against a bookmaker.
        It's a core part of matched betting, and once you understand how it works, it unlocks your ability to <Tooltip content="A lay bet is when you bet against something happening">lay bets</Tooltip>,
        lock in profits, and take full control of your strategy.
      </p>

      <p>
        In this guide, we’ll cover what a betting exchange is, how it works, and why it matters for matched betting.
        We'll also explain <Tooltip content="A back bet is when you bet on something to happen">back bets</Tooltip>,
        <Tooltip content="A lay bet is when you bet against something happening">lay bets</Tooltip>,
        <Tooltip content="The amount you risk losing when placing a lay bet">liability</Tooltip>,
        and <Tooltip content="How much money is available for a market to match your bet">liquidity</Tooltip> — plus review the top exchange platforms.
      </p>

      <h2>🔁 Betting Exchange vs Traditional Bookmaker</h2>
      <p>
        At a bookmaker, you're betting <strong>against the bookie</strong> that an event will happen. These are <span className="back">back</span> bets.
        If your bet wins, they pay you out.
    </p> 
      <h6 className="info">A <span className="back">back</span> bet is a bet that an outcome <em>will</em> happen.</h6>
    <p>
        With a betting exchange, you're betting <strong>against other users</strong>. The exchange just facilitates the bet. You can bet both that an event will happen (a <span className="back">back</span> bet) or that an event <em>will not</em> happen (a <span className="lay">lay</span> bet) on an exchange.
      </p>
      <h6 className="info">A <span className="lay">lay</span> bet is a bet that an outcome <em>will not</em> happen.</h6>
      <p>This makes it possible to both <span className="back">back</span> and <span className="lay">lay</span> bets — essential for matched betting.</p>
       <p>
       When you place a <span className="back">back</span> bet, if you lose, the bookie keeps your <span className="key">stake</span>. If you win, the bookie pays your winnings and returns your <span className="key">stake</span>.
        </p> 
        <p>
       When you place a <span className="lay">lay</span> bet, if you win, you keep <em>their</em> <span className="key">stake</span>. If you lose, you pay out <em>their</em> winnings (called <Tooltip content="The amount you must risk losing when laying a bet">liability</Tooltip>) and return <em>their</em> <span className="key">stake</span>.
        </p> 

      <h2>⚙️ How Does a Betting Exchange Work?</h2>
      <p>
        You can take either side of a bet:
      </p>
      <ImageInline
    imageSrc={crybri}
    alt="football odds example on Smarkets"
    annotations={
        <div>
        <ul>
          <li><strong className="back">Back bet</strong> (green column): betting something <em>will</em> happen.</li>
          <li><strong className="lay">Lay bet</strong> (blue column): betting something <em>will not</em> happen.</li>
        </ul>
        </div>
    }
    />
    <p>
        Odds are set by users — not the exchange — so you can often get better value than with bookies.
      </p>
      <h6 className="info">
      Matched betting relies on placing a <span className="back">back</span> bet at a bookmaker and a <span className="lay">lay</span> bet at a betting exchange to cover all outcomes and lock in profit.
      </h6>

      <CTALarge 
        to="/lay-bet-calculator" 
        text="Go to Lay Bet Calculator" 
        icon={Calculator} 
      />

      <h2>What is Liability?</h2>
      <h6 className="info">
        Liability = The amount you will pay out to your backer if your <span className="lay">lay</span> bet loses.
      </h6>      
      <pre>
        <code>Liability = (Lay odds - 1) × Lay stake</code>
        </pre>
      <div className="example-container">
        <h4>Example</h4>
        <p>
          Lay £10 on Team A at odds of 3.0<br />
          Liability = (3.0 - 1) × £10 = £20<br />
          If Team A wins, you lose £20.
        </p>
      </div>
      <h6 className="info">Liability is taken from your balance when you place the bet and is held by the exchange until the bet settles. It can be alarming when you first see it, but don't worry – if you win, you'll get it back; if you lose, you'll win your <span className="back">back</span> bet.</h6>
      <h6 className="warn">Your exchange account needs enough funds to cover this liability, or the bet won’t be accepted.</h6>

      <h2>What is Liquidity?</h2>
      <h6 className="info">Liquidity = Money available in the market to match your bet.</h6>

      <ImageInline
        imageSrc={bux}
        alt="NBA odds example on Smarkets"
        annotations={
            <div>
            <p>Liquidity is shown below the odds for each outcome.</p>
            <div className="example-container">
              <h4>Example</h4>
              <p>If you wanted to <span className="lay">lay</span> £20 on the Milwaukee Bucks at 1.94, you could do so immediately.</p>
              <p>But if you wanted to <span className="lay">lay</span> Miami Heat
              at 2.14, your maximum lay would be £12.</p>
            </div>
            </div>
        }
        />
      <p>
        Don’t panic if there's not enough liquidity — liquidity often increases closer to the event.
      </p>

      <h2>🧮 Commission: How Exchanges Make Money</h2>
      <p>
        Exchanges charge a small commission on your <strong>winnings</strong> (usually 0–5%).
        <br /><em>Example:</em> You win £15. At 2% commission, you pay £0.30 and keep £14.70.
      </p>
      <p>
        Many exchanges offer <strong>0% commission for new users</strong> — perfect for maximising profit on signup offers.
      </p>

      <h2>🏆 Best Betting Exchanges for Matched Betting (UK)</h2>

      <h6 className="key-info">You’ll want more than one exchange account for the best odds and liquidity.</h6>

      <h3>🟡 Betfair Exchange</h3>
      <img src={bf} alt="Betfair" />
      <ul>
        <li><strong>Commission:</strong> 5% (or 2% on Basic Rewards)</li>
        <li><strong>Pros:</strong> Huge liquidity, reliable, great in-play</li>
        <li><strong>Cons:</strong> Higher default commission</li>
      </ul>

      <h3>⚫ Smarkets</h3>
      <img src={sm} alt="Smarkets" />

      <ul>
        <li><strong>Commission:</strong> 2% (0% for 60 days via promotions)</li>
        <li><strong>Pros:</strong> Good app, clean interface, solid liquidity</li>
        <li><strong>Cons:</strong> Slightly smaller market than Betfair</li>
      </ul>

      <h3>🔴 Matchbook</h3>
      <img src={mb} alt="Matchbook" />

      <ul>
        <li><strong>Commission:</strong> 2% (often 0% via offers)</li>
        <li><strong>Pros:</strong> Competitive odds, user-friendly</li>
        <li><strong>Cons:</strong> Fewer events than others</li>
      </ul>

      <h3>🟣 Betdaq</h3>
      <img src={bd} alt="Betdaq" />

      <ul>
        <li><strong>Commission:</strong> 2% (0% for 100 days)</li>
        <li><strong>Pros:</strong> Good for major markets, strong customer support</li>
        <li><strong>Cons:</strong> Lower liquidity overall, less polished</li>
      </ul>

      <p>🔗 <strong>Top tip:</strong> Use multiple 0% commission offers to keep more profit.</p>

      <h2>Matched Betting Using a Betting Exchange</h2>
  <h3>Option 1: Find Matches Manually</h3>
  
  <ol>
    <li>
      <strong>Find Qualifying Events:</strong>
      <p>We're looking for a football match to bet on to qualify for a £10 free bet at <Tooltip content="A popular bookmaker">Paddy Power</Tooltip>.</p>
      <p>
        Look for an event where you can place your <span className="back">back</span> bet, that qualifies for the offer. You find an Arsenal v Chelsea match.
      </p>
      <img src={cheTotBack} alt="Paddy Power Chelsea v Tottenham football odds" />
    </li>
    <li>
      <strong>Manually Compare Odds:</strong>
      <p>
        You find the same event at Smarkets.
      </p>
      <img src={cheTotLay} alt="Smarkets Chelsea v Tottenham football lay odds" />
    </li>
  </ol>
      <h6 className="warn">The <span className="lay">lay</span> odds are on the right in blue on Smarkets.</h6>
      <p>
        You use the <Link to={"/lay-stake-calculator"}>free bet calculator</Link> to assess how close the match is. You'd retain 93.6% of your stake by <span className="back">backing</span> and <span className="lay">laying</span> Chelsea, so it's an acceptable match. Your qualifying loss is 64p.
      </p>

      
  <h3>Option 2: Get a Tool That Finds Matches For You</h3>
  <p>
    Manual matching is tedious and time-consuming. Most matched bettors use a tool to simplify the process.
  </p>
  <p>
    Oddsmonkey is the most popular tool in the UK, and it helps matched bettors find efficient matches at scale and place bets with a click.
  </p>
  <img src={om} alt="Oddsmonkey Matched Betting Tool" />
      <BannerCTA 
        to="/calculators/lay-stake-calculator" 
        text="Try the Lay Stake Calculator" 
        icon={ArrowUpRight} 
      />

      <h2>FAQs</h2>
      <p><strong>Do I need a betting exchange for matched betting?</strong><br />Yes. Without a lay bet, you can’t guarantee profit.</p>
      <p><strong>Can I lose money on a betting exchange?</strong><br />Yes – especially with lay bets. Always check your maths and your liability.</p>
      <p><strong>What happens if my lay bet isn’t matched?</strong><br />It won’t go through. You can wait for more liquidity or adjust your odds.</p>
      <p><strong>Which exchange should I start with?</strong><br />Betfair and Smarkets are best for beginners with solid liquidity and promos.</p>
    </div>
  );
};

export default BettingExchangeGuide;
